/**
 * QR menünün dilleri ve arayüz yazıları.
 *
 * Ürün, kategori ve başlık çevirileri veritabanından geliyor
 * (menu_translations); buradakiler yalnızca menünün kendi düğme ve uyarıları.
 */

export type MenuLang = 'tr' | 'en' | 'ar' | 'de' | 'ru' | 'id'

/** Seçicide görünen sıra; her dil kendi adıyla. */
export const MENU_LANGS: { code: MenuLang; label: string }[] = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ru', label: 'Русский' },
  { code: 'id', label: 'Bahasa Indonesia' },
]

export const RTL: MenuLang[] = ['ar']

interface Strings {
  menu: string
  search: string
  favorites: string
  soldOut: string
  noResult: string
  empty: string
  addFav: (name: string) => string
  removeFav: (name: string) => string
  language: string
  /** Kampanya penceresi. */
  campaign: string
  campaigns: string
  /** Kampanya penceresinde önceki / sonraki düğmesi. */
  prev: string
  next: string
  toMenu: string
  wholeMenu: string
  off: string
  buyPay: (buy: number, pay: number) => string
  over: (amount: string) => string
  more: (n: number) => string
  daily: string
  until: (date: string) => string
  /** Google değerlendirme: başlıktaki düğme, menü sonundaki kart, hesap penceresi. */
  review: ReviewStrings
  /** Masadan sipariş (QR Sipariş modülü). */
  order: OrderStrings
}

export interface ReviewStrings {
  /** Başlıktaki küçük düğme. */
  short: string
  title: string
  sub: string
  /** Google'a giden düğme. */
  cta: string
}

export interface OrderStrings {
  add: (name: string) => string
  less: (name: string) => string
  addToCart: string
  notes: string
  /** Porsiyon seçiminin başlığı. */
  portion: string
  notePlaceholder: string
  askStaff: string
  callWaiter: string
  askBill: string
  waiterShort: string
  billShort: string
  cart: string
  items: (n: number) => string
  send: string
  sendHint: string
  dealHint: string
  bill: string
  billEmpty: string
  total: string
  paid: string
  remaining: string
  payHow: string
  cash: string
  card: string
  requestBill: string
  cancel: string
  close: string
  failed: string
  tooMany: string
  waiterPending: [string, string]
  waiterSeen: [string, string]
  billPending: [string, string]
  billSeen: [string, string]
  orderPending: [string, string]
  orderAccepted: [string, string]
  orderRejected: [string, string]
}

export const STRINGS: Record<MenuLang, Strings> = {
  tr: {
    menu: 'Menü',
    search: 'Ürün ara',
    favorites: 'Favorilerim',
    soldOut: 'Tükendi',
    noResult: 'Aradığınız üründe sonuç yok.',
    empty: 'Menüde henüz ürün yok.',
    addFav: (n) => `${n} favorilere ekle`,
    removeFav: (n) => `${n} favorilerden çıkar`,
    language: 'Dil',
    campaign: 'Kampanya',
    campaigns: 'Kampanyalar',
    prev: 'Önceki',
    next: 'Sonraki',
    toMenu: 'Menüye geç',
    wholeMenu: 'Bütün menüde geçerli',
    off: 'indirim',
    buyPay: (b, p) => `${b} al ${p} öde`,
    over: (a) => `${a} üstü`,
    more: (n) => `+${n} ürün daha`,
    daily: 'Her gün',
    until: (d) => `${d} tarihine kadar`,
    review: {
      short: 'Bizi değerlendirin',
      title: 'Memnun kaldınız mı?',
      sub: 'Yorumunuz bize çok yardımcı olur.',
      cta: "Google'da değerlendirin",
    },
    order: {
      waiterShort: 'Garson',
      billShort: 'Hesap',
      add: (n) => `${n} ekle`,
      less: (n) => `${n} bir azalt`,
      addToCart: 'Sepete ekle',
      notes: 'Not',
      portion: 'Porsiyon',
      notePlaceholder: 'Not ekleyin, ör. az pişmiş',
      askStaff: 'Bu ürün için garsona sorun.',
      callWaiter: 'Garson çağır',
      askBill: 'Hesap iste',
      cart: 'Sepetim',
      items: (n) => `${n} ürün`,
      send: 'Siparişi gönder',
      sendHint: 'Siparişiniz garson onaylayınca mutfağa iletilir.',
      dealHint: 'Kampanya indirimleri hesapta uygulanır.',
      bill: 'Hesap',
      billEmpty: 'Bu masada henüz açık hesap yok.',
      total: 'Toplam',
      paid: 'Ödenen',
      remaining: 'Kalan',
      payHow: 'Nasıl ödemek istersiniz?',
      cash: 'Nakit',
      card: 'Kart',
      requestBill: 'Hesabı iste',
      cancel: 'Vazgeç',
      close: 'Kapat',
      failed: 'Gönderilemedi. Bağlantınızı kontrol edip tekrar deneyin.',
      tooMany: 'Bekleyen isteğiniz var, garson birazdan gelecek.',
      waiterPending: ['Garson çağrıldı', 'Birazdan masanızda.'],
      waiterSeen: ['Garson geliyor', 'Çağrınız görüldü.'],
      billPending: ['Hesap istendi', 'Garson hesabınızı getirecek.'],
      billSeen: ['Hesabınız geliyor', 'Garson yolda.'],
      orderPending: ['Siparişiniz iletildi', 'Garson onaylayınca hazırlanmaya başlar.'],
      orderAccepted: ['Siparişiniz onaylandı', 'Hazırlanıyor, afiyet olsun.'],
      orderRejected: ['Sipariş onaylanmadı', 'Lütfen garsonla görüşün.'],
    },
  },
  en: {
    menu: 'Menu',
    search: 'Search the menu',
    favorites: 'My favourites',
    soldOut: 'Sold out',
    noResult: 'Nothing matches your search.',
    empty: 'The menu is empty for now.',
    addFav: (n) => `Add ${n} to favourites`,
    removeFav: (n) => `Remove ${n} from favourites`,
    language: 'Language',
    campaign: 'Offer',
    campaigns: 'Offers',
    prev: 'Previous',
    next: 'Next',
    toMenu: 'See the menu',
    wholeMenu: 'Valid on the whole menu',
    off: 'off',
    buyPay: (b, p) => `Buy ${b}, pay for ${p}`,
    over: (a) => `over ${a}`,
    more: (n) => `+${n} more`,
    daily: 'Every day',
    until: (d) => `Until ${d}`,
    review: {
      short: 'Rate us',
      title: 'Enjoyed your visit?',
      sub: 'Your review helps us a lot.',
      cta: 'Review us on Google',
    },
    order: {
      waiterShort: 'Waiter',
      billShort: 'Bill',
      add: (n) => `Add ${n}`,
      less: (n) => `One less ${n}`,
      addToCart: 'Add to order',
      notes: 'Note',
      portion: 'Portion',
      notePlaceholder: 'Add a note, e.g. no onions',
      askStaff: 'Please ask your waiter for this item.',
      callWaiter: 'Call waiter',
      askBill: 'Ask for bill',
      cart: 'My order',
      items: (n) => (n === 1 ? '1 item' : `${n} items`),
      send: 'Send order',
      sendHint: 'Your order goes to the kitchen once the waiter confirms it.',
      dealHint: 'Offer discounts are applied on the bill.',
      bill: 'Bill',
      billEmpty: 'There is no open bill at this table yet.',
      total: 'Total',
      paid: 'Paid',
      remaining: 'Remaining',
      payHow: 'How would you like to pay?',
      cash: 'Cash',
      card: 'Card',
      requestBill: 'Ask for the bill',
      cancel: 'Cancel',
      close: 'Close',
      failed: 'Could not send. Check your connection and try again.',
      tooMany: 'You already have a request waiting, the waiter is on the way.',
      waiterPending: ['Waiter called', 'Someone will be with you shortly.'],
      waiterSeen: ['Waiter is coming', 'Your call was seen.'],
      billPending: ['Bill requested', 'Your waiter will bring the bill.'],
      billSeen: ['Your bill is coming', 'The waiter is on the way.'],
      orderPending: ['Order sent', 'It will be prepared once the waiter confirms.'],
      orderAccepted: ['Order confirmed', 'Being prepared, enjoy your meal.'],
      orderRejected: ['Order not confirmed', 'Please speak to your waiter.'],
    },
  },
  ar: {
    menu: 'القائمة',
    search: 'ابحث في القائمة',
    favorites: 'المفضلة',
    soldOut: 'نفد',
    noResult: 'لا توجد نتائج لبحثك.',
    empty: 'القائمة فارغة حاليًا.',
    addFav: (n) => `أضف ${n} إلى المفضلة`,
    removeFav: (n) => `أزل ${n} من المفضلة`,
    language: 'اللغة',
    campaign: 'عرض',
    campaigns: 'العروض',
    prev: 'السابق',
    next: 'التالي',
    toMenu: 'عرض القائمة',
    wholeMenu: 'يسري على كل القائمة',
    off: 'خصم',
    buyPay: (b, p) => `اشترِ ${b} وادفع ثمن ${p}`,
    over: (a) => `فوق ${a}`,
    more: (n) => `+${n} أصناف أخرى`,
    daily: 'كل يوم',
    until: (d) => `حتى ${d}`,
    review: {
      short: 'قيّمنا',
      title: 'هل استمتعت بزيارتك؟',
      sub: 'تقييمك يساعدنا كثيرًا.',
      cta: 'قيّمنا على Google',
    },
    order: {
      waiterShort: 'النادل',
      billShort: 'الحساب',
      add: (n) => `أضف ${n}`,
      less: (n) => `أنقص ${n}`,
      addToCart: 'أضف إلى الطلب',
      notes: 'ملاحظة',
      portion: 'الحصة',
      notePlaceholder: 'أضف ملاحظة، مثل بدون بصل',
      askStaff: 'يرجى سؤال النادل عن هذا الصنف.',
      callWaiter: 'نادِ النادل',
      askBill: 'اطلب الحساب',
      cart: 'طلبي',
      items: (n) => `${n} صنف`,
      send: 'أرسل الطلب',
      sendHint: 'يُرسل طلبك إلى المطبخ بعد تأكيد النادل.',
      dealHint: 'تُطبق خصومات العروض على الحساب.',
      bill: 'الحساب',
      billEmpty: 'لا يوجد حساب مفتوح على هذه الطاولة بعد.',
      total: 'المجموع',
      paid: 'المدفوع',
      remaining: 'المتبقي',
      payHow: 'كيف تود الدفع؟',
      cash: 'نقدًا',
      card: 'بطاقة',
      requestBill: 'اطلب الحساب',
      cancel: 'إلغاء',
      close: 'إغلاق',
      failed: 'تعذر الإرسال. تحقق من الاتصال وحاول مرة أخرى.',
      tooMany: 'لديك طلب قيد الانتظار، النادل في الطريق.',
      waiterPending: ['تم استدعاء النادل', 'سيكون معك قريبًا.'],
      waiterSeen: ['النادل قادم', 'تمت رؤية طلبك.'],
      billPending: ['تم طلب الحساب', 'سيحضر النادل الحساب.'],
      billSeen: ['الحساب في الطريق', 'النادل قادم.'],
      orderPending: ['تم إرسال الطلب', 'سيُحضّر بعد تأكيد النادل.'],
      orderAccepted: ['تم تأكيد الطلب', 'قيد التحضير، بالهناء والشفاء.'],
      orderRejected: ['لم يُؤكد الطلب', 'يرجى التحدث مع النادل.'],
    },
  },
  de: {
    menu: 'Speisekarte',
    search: 'Speisekarte durchsuchen',
    favorites: 'Meine Favoriten',
    soldOut: 'Ausverkauft',
    noResult: 'Keine Treffer für Ihre Suche.',
    empty: 'Die Speisekarte ist noch leer.',
    addFav: (n) => `${n} zu Favoriten hinzufügen`,
    removeFav: (n) => `${n} aus Favoriten entfernen`,
    language: 'Sprache',
    campaign: 'Aktion',
    campaigns: 'Aktionen',
    prev: 'Zurück',
    next: 'Weiter',
    toMenu: 'Zur Speisekarte',
    wholeMenu: 'Gilt für die ganze Karte',
    off: 'Rabatt',
    buyPay: (b, p) => `${b} kaufen, ${p} zahlen`,
    over: (a) => `ab ${a}`,
    more: (n) => `+${n} weitere`,
    daily: 'Täglich',
    until: (d) => `Bis ${d}`,
    review: {
      short: 'Bewerten Sie uns',
      title: 'Hat es Ihnen gefallen?',
      sub: 'Ihre Bewertung hilft uns sehr.',
      cta: 'Auf Google bewerten',
    },
    order: {
      waiterShort: 'Bedienung',
      billShort: 'Rechnung',
      add: (n) => `${n} hinzufügen`,
      less: (n) => `Ein ${n} weniger`,
      addToCart: 'Zur Bestellung',
      notes: 'Notiz',
      portion: 'Portion',
      notePlaceholder: 'Notiz, z. B. ohne Zwiebeln',
      askStaff: 'Bitte fragen Sie dafür Ihre Bedienung.',
      callWaiter: 'Bedienung rufen',
      askBill: 'Rechnung',
      cart: 'Meine Bestellung',
      items: (n) => (n === 1 ? '1 Artikel' : `${n} Artikel`),
      send: 'Bestellung senden',
      sendHint: 'Ihre Bestellung geht in die Küche, sobald die Bedienung sie bestätigt.',
      dealHint: 'Angebotsrabatte werden auf der Rechnung abgezogen.',
      bill: 'Rechnung',
      billEmpty: 'Für diesen Tisch gibt es noch keine offene Rechnung.',
      total: 'Gesamt',
      paid: 'Bezahlt',
      remaining: 'Offen',
      payHow: 'Wie möchten Sie bezahlen?',
      cash: 'Bar',
      card: 'Karte',
      requestBill: 'Rechnung anfordern',
      cancel: 'Abbrechen',
      close: 'Schließen',
      failed: 'Senden fehlgeschlagen. Bitte Verbindung prüfen und erneut versuchen.',
      tooMany: 'Ihre Anfrage wartet bereits, die Bedienung kommt gleich.',
      waiterPending: ['Bedienung gerufen', 'Gleich ist jemand bei Ihnen.'],
      waiterSeen: ['Bedienung kommt', 'Ihr Ruf wurde gesehen.'],
      billPending: ['Rechnung angefordert', 'Die Bedienung bringt die Rechnung.'],
      billSeen: ['Rechnung kommt', 'Die Bedienung ist unterwegs.'],
      orderPending: ['Bestellung gesendet', 'Sie wird nach Bestätigung zubereitet.'],
      orderAccepted: ['Bestellung bestätigt', 'Wird zubereitet, guten Appetit.'],
      orderRejected: ['Nicht bestätigt', 'Bitte sprechen Sie mit der Bedienung.'],
    },
  },
  ru: {
    menu: 'Меню',
    search: 'Поиск по меню',
    favorites: 'Избранное',
    soldOut: 'Нет в наличии',
    noResult: 'По вашему запросу ничего не найдено.',
    empty: 'Меню пока пустое.',
    addFav: (n) => `Добавить «${n}» в избранное`,
    removeFav: (n) => `Убрать «${n}» из избранного`,
    language: 'Язык',
    campaign: 'Акция',
    campaigns: 'Акции',
    prev: 'Назад',
    next: 'Далее',
    toMenu: 'Перейти к меню',
    wholeMenu: 'Действует на всё меню',
    off: 'скидка',
    buyPay: (b, p) => `${b} по цене ${p}`,
    over: (a) => `от ${a}`,
    more: (n) => `+${n} ещё`,
    daily: 'Каждый день',
    until: (d) => `До ${d}`,
    review: {
      short: 'Оцените нас',
      title: 'Вам понравилось?',
      sub: 'Ваш отзыв очень нам поможет.',
      cta: 'Оставить отзыв в Google',
    },
    order: {
      waiterShort: 'Официант',
      billShort: 'Счёт',
      add: (n) => `Добавить ${n}`,
      less: (n) => `Убрать один ${n}`,
      addToCart: 'В заказ',
      notes: 'Примечание',
      portion: 'Порция',
      notePlaceholder: 'Примечание, напр. без лука',
      askStaff: 'Уточните это блюдо у официанта.',
      callWaiter: 'Позвать официанта',
      askBill: 'Попросить счёт',
      cart: 'Мой заказ',
      items: (n) => `${n} поз.`,
      send: 'Отправить заказ',
      sendHint: 'Заказ уйдёт на кухню после подтверждения официантом.',
      dealHint: 'Скидки по акциям учитываются в счёте.',
      bill: 'Счёт',
      billEmpty: 'Открытого счёта за этим столом пока нет.',
      total: 'Итого',
      paid: 'Оплачено',
      remaining: 'Осталось',
      payHow: 'Как вы хотите оплатить?',
      cash: 'Наличные',
      card: 'Карта',
      requestBill: 'Попросить счёт',
      cancel: 'Отменить',
      close: 'Закрыть',
      failed: 'Не удалось отправить. Проверьте соединение и повторите.',
      tooMany: 'Ваш запрос уже ждёт, официант скоро подойдёт.',
      waiterPending: ['Официант вызван', 'Скоро к вам подойдут.'],
      waiterSeen: ['Официант идёт', 'Ваш вызов принят.'],
      billPending: ['Счёт запрошен', 'Официант принесёт счёт.'],
      billSeen: ['Счёт несут', 'Официант уже идёт.'],
      orderPending: ['Заказ отправлен', 'Начнут готовить после подтверждения.'],
      orderAccepted: ['Заказ подтверждён', 'Готовится, приятного аппетита.'],
      orderRejected: ['Заказ не подтверждён', 'Пожалуйста, обратитесь к официанту.'],
    },
  },
  id: {
    menu: 'Menu',
    search: 'Cari menu',
    favorites: 'Favorit saya',
    soldOut: 'Habis',
    noResult: 'Tidak ada hasil untuk pencarian Anda.',
    empty: 'Menu masih kosong.',
    addFav: (n) => `Tambahkan ${n} ke favorit`,
    removeFav: (n) => `Hapus ${n} dari favorit`,
    language: 'Bahasa',
    campaign: 'Promo',
    campaigns: 'Promo',
    prev: 'Sebelumnya',
    next: 'Berikutnya',
    toMenu: 'Lihat menu',
    wholeMenu: 'Berlaku untuk semua menu',
    off: 'diskon',
    buyPay: (b, p) => `Beli ${b} bayar ${p}`,
    over: (a) => `di atas ${a}`,
    more: (n) => `+${n} lainnya`,
    daily: 'Setiap hari',
    until: (d) => `Sampai ${d}`,
    review: {
      short: 'Beri ulasan',
      title: 'Puas dengan kunjungan Anda?',
      sub: 'Ulasan Anda sangat membantu kami.',
      cta: 'Beri ulasan di Google',
    },
    order: {
      waiterShort: 'Pelayan',
      billShort: 'Tagihan',
      add: (n) => `Tambah ${n}`,
      less: (n) => `Kurangi ${n}`,
      addToCart: 'Tambah ke pesanan',
      notes: 'Catatan',
      portion: 'Porsi',
      notePlaceholder: 'Tambah catatan, mis. tanpa bawang',
      askStaff: 'Silakan tanyakan menu ini ke pelayan.',
      callWaiter: 'Panggil pelayan',
      askBill: 'Minta tagihan',
      cart: 'Pesanan saya',
      items: (n) => `${n} item`,
      send: 'Kirim pesanan',
      sendHint: 'Pesanan dikirim ke dapur setelah dikonfirmasi pelayan.',
      dealHint: 'Diskon promo diterapkan pada tagihan.',
      bill: 'Tagihan',
      billEmpty: 'Belum ada tagihan terbuka di meja ini.',
      total: 'Total',
      paid: 'Dibayar',
      remaining: 'Sisa',
      payHow: 'Bagaimana Anda ingin membayar?',
      cash: 'Tunai',
      card: 'Kartu',
      requestBill: 'Minta tagihan',
      cancel: 'Batal',
      close: 'Tutup',
      failed: 'Gagal mengirim. Periksa koneksi lalu coba lagi.',
      tooMany: 'Permintaan Anda sedang menunggu, pelayan segera datang.',
      waiterPending: ['Pelayan dipanggil', 'Segera datang ke meja Anda.'],
      waiterSeen: ['Pelayan datang', 'Panggilan Anda sudah dilihat.'],
      billPending: ['Tagihan diminta', 'Pelayan akan membawa tagihan.'],
      billSeen: ['Tagihan sedang dibawa', 'Pelayan dalam perjalanan.'],
      orderPending: ['Pesanan terkirim', 'Disiapkan setelah dikonfirmasi pelayan.'],
      orderAccepted: ['Pesanan dikonfirmasi', 'Sedang disiapkan, selamat menikmati.'],
      orderRejected: ['Pesanan tidak dikonfirmasi', 'Silakan bicara dengan pelayan.'],
    },
  },
}

const KEY = 'adissoo.menuLang'

/** Müşterinin daha önce seçtiği dil; yoksa telefonun dili; o da yoksa Türkçe. */
export function initialLang(): MenuLang {
  const known = (c: string | null | undefined): MenuLang | null =>
    MENU_LANGS.find((l) => l.code === c)?.code ?? null
  try {
    const saved = known(localStorage.getItem(KEY))
    if (saved) return saved
  } catch {
    // Depolama yoksa telefonun diline bakılıyor.
  }
  for (const l of navigator.languages ?? [navigator.language]) {
    const hit = known(l?.slice(0, 2).toLowerCase())
    if (hit) return hit
  }
  return 'tr'
}

export function rememberLang(lang: MenuLang) {
  try {
    localStorage.setItem(KEY, lang)
  } catch {
    // Seçim yalnızca bu açılışta kalır.
  }
}
